import { useFocusEffect } from '@react-navigation/native';
import { useGlobalConfig } from 'context/GlobalContext';
import { useSocket } from 'context/SocketContext';
import { useMobileOrDesktop } from 'hoc/mobileOrDesktop';
import { RootStackScreenProps } from 'navigation/AppNavigator';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppState, AppStateStatus, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useAnalytics } from 'services/analytics/useAnalytics';
import { ProgramType } from 'utils/enum';
import { calcSizeForDevice } from 'utils/index';
import { Platform } from 'utils/platform';
import { modifyVastUrl } from 'utils/vast';

import Button from 'components/Button';
import Hero from 'components/Hero';
import Player from 'components/Player';
import { useStream } from 'components/Player/Stream/context';
import { usePlayer } from 'components/Player/context';
import ProgramContent from 'components/Program/Content';
import ProgramHeader from 'components/Program/Header';
import ProgramRelated from 'components/Program/Related';
import ProgramSave from 'components/Program/Save';
import ProgramShared from 'components/Program/Shared';
import { useProgram } from 'components/Program/context';
import IsLoading from 'components/isLoading';

const MovieScreen: React.FC<RootStackScreenProps<'Program'>> = ({ navigation }) => {
  const {
    isPlaying,
    isVisible,
    isFullScreen,
    isStreaming,
    pause,
    play,
    setIsVisible,
    mute,
    setNextTrack,
  } = usePlayer();

  const program = useProgram();
  const { watch } = useSocket();
  const analytics = useAnalytics();
  const { ads } = useGlobalConfig();
  const { isConnected } = useStream();
  const [isActive, setIsActive] = useState(false);
  const [mediaId, setMediaId] = useState<string>();
  const [startTime, setStartTime] = useState<number>();
  const [isDataReady, setIsDataReady] = useState<boolean>(false);
  const [appState, setAppState] = useState(AppState.currentState);
  const buttonRef = useRef<TouchableOpacity>(null);

  useEffect(() => {
    setNextTrack(undefined);
  }, []);

  useEffect(() => {
    return navigation.addListener('beforeRemove', async () => {
      if (isPlaying && !isStreaming) {
        pause();
      }
      mute(false);
    });
  }, [navigation, isPlaying, isStreaming, pause]);

  useFocusEffect(
    useCallback(() => {
      setIsActive(true);

      return () => {
        if (!isStreaming) pause();
        setIsActive(false);
      };
    }, [isStreaming])
  );

  useFocusEffect(
    useCallback(() => {
      analytics.triggerMovieScreenPageViewEvent({
        programId: program._id,
        programTitle: program.title,
        programAgeRating: program.rating,
        programAdUnit: program.ads?.adunit?.val!,
        programCategory: program.category.map((tag) => tag.name).join(', '),
        programProducer: program.ads?.adunit?.name,
        programDirector: program.director,
        programGenre: program.genre.map((genre) => genre.name).join(', '),
        programYear: program.year,
      });
    }, [])
  );

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState: AppStateStatus) => {
      if (appState.match(/inactive|background/) && nextAppState === 'active') {
        if (Platform.isAndroidTV && buttonRef?.current) {
          buttonRef?.current?.requestTVFocus();
        }
      }
      setAppState(nextAppState);
    });

    return () => subscription.remove();
  }, [appState]);

  const playerProps: PlayerProps | undefined = useMemo(() => {
    if (!program) {
      return undefined;
    }
    const mediaSource = program.mediaSource;

    const media: PlayerMedia = {
      title: program.title,
      image: [program.imageVertical?.url!, program.imageHorizontal?.url!],
      duration: program.duration,
      programId: program._id,
      programTitle: program.title,
      programUrl: program.url,
      ratingValue: program.rating,
      ratingDescription: program.rateDescription,
      videoTracks: [],
      mediaSource,
    };

    const mediaIdDub = program.mediaIdDub;
    const mediaIdDubSpalla = program.videoIdDub;

    if (mediaIdDub) {
      media.videoTracks.push({
        language: 'dub',
        trackId: mediaSource ? mediaIdDubSpalla : mediaIdDub,
      });
    }

    media.videoTracks.push({
      language: 'origin',
      trackId: mediaSource ? program.videoId : program.mediaId,
    });

    return {
      media,
      isLive: program.type === ProgramType.LIVE || program.type === ProgramType.RADIO,
      adTagUrl:
        ads?.disableAds || program.ads?.disableAds
          ? undefined
          : Platform.isWebTV
          ? ads?.ctvVast
          : modifyVastUrl(ads?.vast, program),
      adsExpirationTime: ads?.adsExpirationTime,
    };
  }, [program, mediaId, isConnected]);

  useEffect(() => {
    if (!playerProps || playerProps.isLive) {
      return;
    }

    watch(playerProps).then((data) => {
      if (data) {
        setStartTime(data.watchedTime);
      }
      setIsDataReady(true);
    });
  }, [playerProps]);

  const Video = useMemo(
    () =>
      mediaId !== undefined && isActive && playerProps !== undefined ? (
        <Player {...playerProps} startTime={startTime} autoplay />
      ) : undefined,
    [mediaId, isActive, playerProps]
  );

  const [VideoMobile, VideoDesktop] = useMobileOrDesktop(Video, Video);

  if (Platform.isAndroidTV && isVisible) {
    return VideoDesktop;
  }

  return (
    <>
      {VideoMobile !== undefined && (
        <View style={[{ marginTop: isFullScreen ? 0 : Platform.isMobileWeb() ? 45 : 80 }]}>
          {Video}
        </View>
      )}
      {(Platform.isAndroidTV || Platform.OS === 'web' || !isFullScreen) && (
        <Hero program={program} hideCover={mediaId !== undefined}>
          <Hero.Header
            program={program}
            mediaId={mediaId}
            Aside={
              VideoDesktop !== undefined && !Platform.isAndroidTV ? (
                <View style={[styles.videoBox, { width: Platform.isTV ? 0 : '80%' }]}>{Video}</View>
              ) : undefined
            }>
            <ProgramHeader />
            {!isDataReady ? (
              <View style={styles.loadingContainer}>
                <IsLoading />
              </View>
            ) : (
              <Button
                id="PlayBtn"
                ref={buttonRef}
                useTVFocus
                hasTVPreferredFocus
                style={{
                  ...(Platform.OS === 'web' || Platform.isTV
                    ? {
                        width: calcSizeForDevice(300, 400),
                        display: mediaId && !Platform.isTV ? 'none' : undefined,
                        height: calcSizeForDevice(40, 52),
                      }
                    : {
                        display: mediaId && !Platform.isTV ? 'none' : undefined,
                        height: 40,
                        fontFamily: 'system-ui',
                      }),
                }}
                textStyle={{ fontSize: calcSizeForDevice(18, 24) }}
                iconStyle={{ width: calcSizeForDevice(24, 30), height: calcSizeForDevice(24, 30) }}
                iconType={Platform.OS !== 'web' && !Platform.isTV ? 'Play' : undefined}
                variant="gradient"
                size="md"
                isFullWidth={Platform.OS !== 'web' && !Platform.isTV}
                onPress={() => {
                  if (!mediaId) setMediaId(program.mediaId || program.videoId);

                  analytics.triggerMediaPlayActionEvent({
                    programId: program.id,
                    programTitle: program.title,
                    programAgeRating: program.rating,
                    programAdUnit: program.ads?.adunit?.val!,
                    programCategory: program.category?.map((tag) => tag.name).join(', '),
                    programProducer: program.ads?.adunit?.name,
                    programDirector: program.director,
                    programGenre: program.genre.map((genre) => genre.name).join(', '),
                    programYear: program.year,
                  });

                  play();

                  if (Platform.isAndroidTV) setIsVisible(true);
                }}>
                {startTime
                  ? 'Continuar assistindo'
                  : Platform.OS !== 'web'
                  ? 'Assistir'
                  : 'Assistir agora'}
              </Button>
            )}
            <View style={styles.actionsContainer}>
              <ProgramSave />
              <ProgramShared />
            </View>
            <ProgramContent />
          </Hero.Header>

          <View style={styles.relatedContainer}>
            <ProgramRelated />
          </View>
        </Hero>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  videoBox: {
    aspectRatio: 1 / 0.56,
  },
  loadingContainer: {
    width: calcSizeForDevice(300, 400),
    height: calcSizeForDevice(40, 52),
  },
  actionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: calcSizeForDevice(10, 14),
    marginBottom: calcSizeForDevice(10, 20),
    gap: 8, // Equivalente ao space="md"
  },
  relatedContainer: {
    paddingVertical: calcSizeForDevice(24),
  },
});

export default MovieScreen;
